import React from 'react';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { BodyText, Button, DisplayText, LabelText, useResponsive } from '@garnish/core';
import { ContentfulImageTransformOptions, FallbackImage } from '@garnish/media';

import { assets } from '../../assets';
import { FlexLandingPageContent } from './FlexLandingPageContent';

export const FlexLandingPageCoverage = (props: FlexLandingPageCoverageProps) => {
  const {
    coverageHint,
    coverageTitle,
    coverageDescription,
    coverageImageUrl,
    coverageImageA11y,
    coverageCta,
    isLoading,
    onCtaPress,
  } = props;

  const { currentBreakpoint } = useResponsive();

  if (currentBreakpoint.isXS) {
    return (
      <FlexLandingPageContent style={styles.containerXS}>
        <View style={styles.contentInfoText}>
          <LabelText sizeMatch={['14']} style={styles.label}>
            {coverageHint}
          </LabelText>

          <DisplayText sizeMatch={['32']} style={styles.fontColor}>
            {coverageTitle}
          </DisplayText>
        </View>

        <FallbackImage
          source={assets.wrapBanner}
          baseUrl={coverageImageUrl}
          aria-label={coverageImageA11y}
          style={styles.imageXS}
          contentfulOptions={CONTENTFUL_OPTIONS_XS}
        />

        <BodyText sizeMatch={['16']} style={styles.fontColor}>
          {coverageDescription}
        </BodyText>

        <Button
          size="large"
          palette="cucumber"
          style={styles.ctaXS}
          isLoading={isLoading}
          onPress={onCtaPress}
        >
          {coverageCta}
        </Button>
      </FlexLandingPageContent>
    );
  }

  return (
    <FlexLandingPageContent style={styles.containerSM}>
      <View style={styles.contentSM}>
        <View style={styles.contentInfoSM}>
          <FallbackImage
            source={assets.wrapBanner}
            baseUrl={coverageImageUrl}
            aria-label={coverageImageA11y}
            style={styles.imageSM}
            contentfulOptions={CONTENTFUL_OPTIONS_SM}
          />
        </View>

        <View style={styles.contentInfoSM}>
          <View style={styles.contentInfoText}>
            <LabelText sizeMatch={['14']} style={styles.label}>
              {coverageHint}
            </LabelText>

            <DisplayText sizeMatch={['40']} style={styles.fontColor}>
              {coverageTitle}
            </DisplayText>

            <BodyText sizeMatch={['18']} style={styles.fontColor}>
              {coverageDescription}
            </BodyText>
          </View>

          <Button
            size="large"
            palette="cucumber"
            style={styles.ctaSM}
            isLoading={isLoading}
            onPress={onCtaPress}
          >
            {coverageCta}
          </Button>
        </View>
      </View>
    </FlexLandingPageContent>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const IMAGE_HEIGHT_XS = 335;
const CONTENTFUL_OPTIONS_XS: Partial<ContentfulImageTransformOptions> = {
  h: IMAGE_HEIGHT_XS,
  q: 70,
  fm: 'webp',
  fit: 'fill',
};

const IMAGE_HEIGHT_SM = 384;
const CONTENTFUL_OPTIONS_SM: Partial<ContentfulImageTransformOptions> = {
  h: IMAGE_HEIGHT_SM,
  q: 70,
  fm: 'webp',
  fit: 'fill',
};

const styles = StyleSheet.create({
  containerXS: {
    paddingVertical: theme.spacing['2'],
    gap: theme.spacing['8'],
  },
  containerSM: {
    paddingVertical: theme.spacing['8'],
    gap: theme.spacing['10'],
  },
  contentSM: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: theme.spacing['20'],
  },
  contentInfoSM: {
    flex: 1,
    gap: theme.spacing['6'],
  },
  contentInfoText: {
    gap: theme.spacing['2'],
  },
  fontColor: {
    color: theme.colors.CHARCOAL,
  },
  label: {
    alignSelf: 'flex-start',
    color: theme.colors.SPINACH,
  },
  ctaXS: {
    height: 56,
    minWidth: 200,
  },
  ctaSM: {
    minWidth: 200,
    height: 60,
    alignSelf: 'flex-start',
  },
  imageXS: {
    width: '100%',
    height: IMAGE_HEIGHT_XS,
    borderRadius: theme.radius.large,
  },
  imageSM: {
    width: '100%',
    height: IMAGE_HEIGHT_SM,
    borderRadius: theme.radius.large,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type FlexLandingPageCoverageProps = {
  coverageHint: string;
  coverageTitle: string;
  coverageDescription: string;
  coverageImageUrl?: string;
  coverageImageA11y?: string;
  coverageCta: string;
  isLoading: boolean;
  onCtaPress: () => void;
};
